"use client";

import { useEffect } from "react";
import { useRouter } from "next/navigation";
import { useTheme } from "@/components/theme-provider";
import { PREVIEW_QUERY_PARAM } from "@/lib/theme-presets";

// Мост для страницы сравнения тем (/theme-compare). Активен ТОЛЬКО когда
// портал открыт во фрейме: сообщает родителю о каждой смене маршрута и
// исполняет его команды (навигация SPA-переходом — тема и состояние не
// слетают; смена light/dark). Благодаря этому оба пейна ходят по порталу
// синхронно. Вне фрейма — ничего не делает.
export const COMPARE_MSG_SOURCE = "yuc-theme-compare";

function currentPath(): string {
  const params = new URLSearchParams(window.location.search);
  params.delete(PREVIEW_QUERY_PARAM);
  const qs = params.toString();
  return window.location.pathname + (qs ? `?${qs}` : "");
}

export function ThemeCompareBridge() {
  const router = useRouter();
  const { setMode } = useTheme();

  useEffect(() => {
    if (window.self === window.top) return;

    const report = () => {
      window.parent.postMessage(
        { source: COMPARE_MSG_SOURCE, type: "route", path: currentPath() },
        window.location.origin,
      );
    };

    // pushState/replaceState не дают событий — оборачиваем, чтобы ловить
    // и смену query (вкладки внутри модулей), а не только pathname.
    const origPush = history.pushState;
    const origReplace = history.replaceState;
    const wrap = (fn: typeof history.pushState): typeof history.pushState =>
      function (this: History, ...args) {
        const result = fn.apply(this, args);
        queueMicrotask(report);
        return result;
      };
    history.pushState = wrap(origPush);
    history.replaceState = wrap(origReplace);
    window.addEventListener("popstate", report);

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data as { source?: string; type?: string; path?: string; mode?: string } | null;
      if (!data || data.source !== COMPARE_MSG_SOURCE) return;
      if (data.type === "navigate" && typeof data.path === "string" && data.path !== currentPath()) {
        router.push(data.path);
      }
      if (data.type === "set-mode" && (data.mode === "light" || data.mode === "dark")) {
        setMode(data.mode);
      }
    };
    window.addEventListener("message", onMessage);
    report();

    return () => {
      history.pushState = origPush;
      history.replaceState = origReplace;
      window.removeEventListener("popstate", report);
      window.removeEventListener("message", onMessage);
    };
  }, [router, setMode]);

  return null;
}
